/** Title-case a string, respecting Arabic/Hispanic name particles */
export const titleCase = (s) => {
  const particles = new Set([
    "al", "bin", "ibn", "de", "van", "von", "da", "di", "el", "la", "le",
  ]);
  return s
    .split(/\s+/)
    .map((w, i) => {
      if (w.includes("-")) {
        return w
          .split("-")
          .map((part, j) => {
            if (j > 0 && particles.has(part.toLowerCase())) return part.toLowerCase();
            return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
          })
          .join("-");
      }
      if (i > 0 && particles.has(w.toLowerCase())) return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(" ");
};

export const parseCardText = (rawText) => {
  // ── Step 1: Aggressive OCR normalisation ────────────────────────
  let normalised = rawText
    .replace(/\r\n?/g, "\n")
    .replace(/\t/g, " ")
    .replace(/[""]/g, '"')
    .replace(/['']/g, "'")
    .replace(/[|]/g, "l")          // pipe → lowercase L
    .replace(/[~`]/g, "-")         // tilde → dash
    .replace(/\u00A0/g, " ")       // non-breaking space → space
    .replace(/ +/g, " ");

  const lines = normalised
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 1);

  const flat = lines.join(" ");

  // ── Step 2: Regex patterns ──────────────────────────────────────
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
  const websiteRegex =
    /(?:https?:\/\/)?(?:www\.)?([a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?)/i;

  const phoneLabelRegex =
    /^(?:tel\.?|telephone|phone|mobile|cell|fax|whatsapp|call|number|tel\.?\s*no\.?|ph(?:one)?\.?\s*(?:no\.?|#)?)\s*[:\-]?\s*|^[mfpt]\s*[:]\s*/i;
  const phoneDigitRegex = /(\+?\d[\d\s().\-]{5,}\d)/;

  const designationLabelRegex =
    /^(?:designation|position|role|job\s*title|title|profession|occupation|department|job)\s*[:\-]?\s*/i;

  const nameLabelRegex =
    /^(?:name|full\s*name|contact|your\s*name|person)\s*[:\-]?\s*/i;

  const companySuffixRegex =
    /\b(?:llc|ltd|l\.?l\.?c|inc\.?|corp\.?|corporation|group|properties|real\s*estate|realty|trading|services|solutions|enterprises|holdings|consulting|consultancy|fz-?co|technical|technology|technologies|systems|international|global|worldwide|co\.?)\b/i;

  const addressRegex =
    /\b(?:street|st\.?|avenue|ave\.?|road|rd\.?|boulevard|blvd\.?|suite|floor|building|tower|office|block|p\.?o\.?\s*box|lane|drive|way|court|place|circle|parkway|highway|hwy)\b/i;

  const usStateRegex =
    /\b(?:alabama|al|alaska|ak|arizona|az|arkansas|ar|california|ca|colorado|co|connecticut|ct|delaware|de|florida|fl|georgia|ga|hawaii|hi|idaho|id|illinois|il|indiana|in|iowa|ia|kansas|ks|kentucky|ky|louisiana|la|maine|me|maryland|ma|massachusetts|ma|michigan|mi|minnesota|mn|mississippi|ms|missouri|mo|montana|mt|nebraska|ne|nevada|nv|new\s*hampshire|nh|new\s*jersey|nj|new\s*mexico|nm|new\s*york|ny|north\s*carolina|nc|north\s*dakota|nd|ohio|oh|oklahoma|ok|oregon|or|pennsylvania|pa|rhode\s*island|ri|south\s*carolina|sc|south\s*dakota|sd|tennessee|tn|texas|tx|utah|ut|vermont|vt|virginia|va|washington|wa|west\s*virginia|wv|wisconsin|wi|wyoming|wy|dc)\b/i;

  const locationRegex =
    /\b(?:dubai|abu\s*dhabi|sharjah|ajman|umm\s*al\s*quwain|ras\s*al\s*khaimah|fujairah|uae|united\s*arab\s*emirates|india|pakistan|usa|uk|canada|australia|germany|france|japan|china|brazil|singapore|malaysia|philippines|saudi\s*arabia|oman|bahrain|kuwait|qatar|turkey|egypt|nigeria|kenya|south\s*africa|ireland|scotland|england|wales)\b/i;

  const cityStateRegex = /^[A-Z][a-z]+(?:\s[A-Z][a-z]+)*\s*,\s*[A-Z]{2}$/;
  const zipRegex = /\b\d{5}(?:-\d{4})?\b/;
  const heavyCommaRegex = /.*,.*,/;

  const nonNameFieldRegex =
    /\b(?:software\s+engineering|computer\s+science|mechanical\s+engineering|electrical\s+engineering|civil\s+engineering|information\s+technology|business\s+administration|data\s+science|artificial\s+intelligence|machine\s+learning|web\s+development|mobile\s+development|digital\s+marketing|project\s+management|human\s+resources|supply\s+chain|quality\s+assurance|customer\s+service|business\s+development|public\s+relations|content\s+writing|graphic\s+design|interior\s+design|financial\s+analysis|risk\s+management|operations\s+management|strategic\s+planning|market\s+research|brand\s+management|product\s+management|ux\s+design|ui\s+design|backend\s+development|frontend\s+development|full[\s-]stack|cloud\s+computing|cyber\s+security|network\s+engineering|database\s+administration|system\s+administration|devops|blockchain|quantum\s+computing)\b/i;

  const designationKeywords = [
    "chief executive officer", "chief technology officer",
    "chief financial officer", "chief operating officer",
    "chief marketing officer", "chief information officer",
    "vice president", "managing director",
    "senior software engineer", "product manager",
    "head of marketing", "head of sales", "head of operations",
    "head of finance", "head of design", "head of engineering",
    "head of product", "head of hr", "head of technology",
    "head of business", "head of strategy",
    "co-founder", "co founder", "cofounder",
    "senior engineer", "senior designer", "senior developer",
    "senior consultant", "senior manager", "senior analyst",
    "senior specialist", "senior advisor",
    "software engineer", "software developer",
    "graphic designer", "web developer", "mobile developer",
    "full stack developer", "frontend developer", "backend developer",
    "devops engineer", "data engineer", "data scientist",
    "business analyst", "project manager", "program manager",
    "account manager", "account executive",
    "sales manager", "sales executive", "sales director",
    "marketing manager", "marketing director", "marketing specialist",
    "hr manager", "hr director",
    "finance manager", "finance director",
    "operations manager", "operations director",
    "legal counsel", "general counsel",
    "chief", "ceo", "cto", "cfo", "coo", "cmo", "cio",
    "founder", "owner", "partner", "director", "manager",
    "engineer", "developer", "designer", "architect",
    "consultant", "officer", "president", "executive",
    "coordinator", "specialist", "supervisor", "analyst",
    "advisor", "associate", "lead", "head", "intern", "trainee",
  ];

  // ── Step 3: Helpers ─────────────────────────────────────────────
  const usedLines = new Set();
  const markUsed = (i) => usedLines.add(i);
  const isUsed = (i) => usedLines.has(i);

  const cleanOcr = (s) =>
    s
      .replace(/[:;|#~`]+$/g, "")
      .replace(/\s+\d+$/, "")
      .replace(/\s+[-–—]+$/, "")
      .replace(/\s{2,}/g, " ")
      .trim();

  const stripLeadingGarbage = (s) =>
    s.replace(/^[[\]——\-–—*#.!?<>{}()\s]+/, "").trim();

  const isAddressOrLocation = (line) => {
    const lower = line.toLowerCase();
    if (addressRegex.test(lower)) return true;
    if (locationRegex.test(lower)) return true;
    if (usStateRegex.test(lower)) return true;
    if (cityStateRegex.test(line)) return true;
    if (zipRegex.test(line)) return true;
    if (heavyCommaRegex.test(line)) return true;
    if (/^\d+\s/.test(line) && /,/.test(line)) return true;
    if (/:\s*\d{3,}/.test(line)) return true;
    if (/^[A-Z][a-z]+(?:\s[A-Z][a-z]+)*\s*,/.test(line) && /\b[A-Z]{2,}\b$/.test(line)) return true;
    return false;
  };

  const isDesignationLine = (line) => {
    const lower = stripDesignationLabel(line).toLowerCase();
    return designationKeywords.some((kw) => {
      const p = new RegExp(
        "\\b" + kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "\\b",
        "i"
      );
      return p.test(lower);
    });
  };

  const isLikelyName = (line) => {
    const cleaned = cleanOcr(line);
    if (!/[a-zA-Z]/.test(cleaned)) return false;
    const words = cleaned.split(/\s+/);
    if (words.length >= 2 && words.length <= 4) {
      const allCapitalised = words.every(
        (w) => /^[A-Z]/.test(w) || /^(?:al|bin|ibn|de|van|von|da|di)$/i.test(w)
      );
      if (allCapitalised) return true;
    }
    if (words.length === 1 && /^[A-Z][a-z]{2,}$/.test(cleaned)) return true;
    if (/^[A-Z]\.\s*[A-Z][a-z]+/.test(cleaned)) return true;
    if (/^[A-Z][a-z]+-[A-Z][a-z]+(\s+[A-Z][a-z]+)*$/.test(cleaned)) return true;
    return false;
  };

  const isAllCaps = (s) => /^[A-Z\s-]+$/.test(s.replace(/[^a-zA-Z\s-]/g, ""));

  const looksLikeLogoOrCompany = (line) => {
    const cleaned = cleanOcr(line);
    if (/[^a-zA-Z0-9\s.&\-]/.test(cleaned)) return true;
    if (companySuffixRegex.test(cleaned)) return true;
    if (/\d/.test(cleaned)) return true;
    return false;
  };

  const stripDesignationLabel = (line) =>
    line.replace(designationLabelRegex, "").trim();

  // ── Step 4: EMAIL ───────────────────────────────────────────────
  let email = "";
  for (let i = 0; i < lines.length; i++) {
    if (isUsed(i)) continue;
    const m = lines[i].match(emailRegex);
    if (m) {
      email = m[0];
      markUsed(i);
      break;
    }
  }

  // ── Step 5: PHONE ───────────────────────────────────────────────
  // Strategy: OCR often splits international numbers across lines, e.g.:
  //   Line 1: "+1"          (country code only)
  //   Line 2: "5550192834"  (local number)
  // We detect the bare country code first, then combine with the local number.
  let phoneNum = "";
  let countryPrefix = "";

  // Regex: a line that is ONLY a country dialing code like "+1", "+971", "001"
  const bareCountryCodeRegex = /^(?:\+|00)?(\d{1,3})$/;

  // Pre-scan: find any bare country-code-only line
  for (let i = 0; i < lines.length; i++) {
    if (isUsed(i)) continue;
    const cleaned = lines[i].replace(phoneLabelRegex, "").trim();
    const cm = cleaned.match(bareCountryCodeRegex);
    if (cm && cm[1].length >= 1 && cm[1].length <= 3) {
      countryPrefix = cm[1];
      markUsed(i);
      break;
    }
  }

  // Pass 1: look for full phone number (7–15 digits) on a line
  for (let i = 0; i < lines.length; i++) {
    if (isUsed(i)) continue;
    const cleaned = lines[i].replace(phoneLabelRegex, "").trim();
    const m = cleaned.match(phoneDigitRegex);
    if (m) {
      const digits = m[0].replace(/\D/g, "");
      if (digits.length >= 7 && digits.length <= 15) {
        if (digits.length === 4 && parseInt(digits) > 1900 && parseInt(digits) < 2100) continue;
        // Combine: if we found a separate country code, prepend it
        phoneNum = "+" + (countryPrefix ? countryPrefix + digits : digits);
        markUsed(i);
        break;
      }
    }
  }

  // Pass 2: fallback — scan flat text for a phone pattern
  if (!phoneNum) {
    const allPhones = flat.match(new RegExp(phoneDigitRegex.source, "g")) || [];
    for (const m of allPhones) {
      const digits = m.replace(/\D/g, "");
      if (digits.length >= 7 && digits.length <= 15) {
        phoneNum = "+" + (countryPrefix ? countryPrefix + digits : digits);
        break;
      }
    }
  }

  // Pass 3: if still nothing, use the bare country code alone (at least sets the flag)
  if (!phoneNum && countryPrefix) {
    phoneNum = "+" + countryPrefix;
  }

  // ── Step 6: DESIGNATION — word-boundary match, prefer shortest ──
  let design = "";
  let designIdx = -1;
  const designCandidates = [];

  for (let i = 0; i < lines.length; i++) {
    if (isUsed(i)) continue;
    const lower = stripDesignationLabel(lines[i]).toLowerCase();
    for (const kw of designationKeywords) {
      const pattern = new RegExp(
        "\\b" + kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "\\b",
        "i"
      );
      if (pattern.test(lower)) {
        designCandidates.push({ line: lines[i], idx: i, len: lines[i].length });
        break;
      }
    }
  }
  if (designCandidates.length > 0) {
    designCandidates.sort((a, b) => b.len - a.len);
    let designLine = stripLeadingGarbage(stripDesignationLabel(designCandidates[0].line));
    designLine = designLine.replace(/\s+(?!of\b|and\b|for\b|in\b|at\b|to\b|the\b|or\b)\w{1,3}$/i, "");
    design = cleanOcr(designLine);
    designIdx = designCandidates[0].idx;
    markUsed(designIdx);
  }

  // ── Step 7: NAME — score-based candidate ranking ────────────────
  const nameCandidates = [];

  for (let i = 0; i < lines.length; i++) {
    if (isUsed(i)) continue;
    const line = lines[i];
    let cleaned = cleanOcr(line);
    if (cleaned.length < 2 || cleaned.length > 40) continue;

    if (emailRegex.test(cleaned)) continue;
    if (websiteRegex.test(cleaned)) continue;
    if (/\d{7,}/.test(cleaned)) continue;
    if (/^[\d\s().\-+]+$/.test(cleaned)) continue;

    if (companySuffixRegex.test(cleaned)) continue;

    // Additional safety: if a top line is all-caps and looks like a logo/company, skip it.
    if (i === 0 && isAllCaps(line) && looksLikeLogoOrCompany(line)) continue;

    if (/^[A-Z0-9\s.&]{3,}$/.test(cleaned) && !/[a-z]/.test(cleaned)) {
      const words = cleaned.split(/\s+/);
      if (words.length === 1) continue;
      cleaned = titleCase(cleaned);
      if (!isLikelyName(cleaned)) continue;
    }

    if (isDesignationLine(cleaned)) continue;
    if (nonNameFieldRegex.test(cleaned)) continue;
    if (isAddressOrLocation(cleaned)) continue;
    if (/^[A-Za-z]+\s*:\s*.+/.test(cleaned)) continue;

    let score = 0;
    const wordCount = cleaned.split(/\s+/).length;

    if (wordCount >= 2 && wordCount <= 4) score += 35;
    else if (wordCount === 1) score += 5;
    else score += 5;

    if (/^[A-Z][a-z]/.test(cleaned)) score += 15;
    if (isLikelyName(cleaned)) score += 45;

    // Top-of-card position bonus
    if (i === 0) score += 8;
    else if (i <= 2) score += 15;
    else if (i <= 4) score += 8;

    // Proximity to designation line
    if (designIdx >= 0) {
      const dist = Math.abs(i - designIdx);
      if (dist === 1) score += 25;
      else if (dist <= 3) score += 15;
      else if (dist <= 5) score += 5;
    }

    if (/\d/.test(cleaned)) score -= 10;
    if (/[@#$%^&*{}|\\]/.test(cleaned)) score -= 15;
    if ((cleaned.match(/,/g) || []).length >= 2) score -= 30;
    if (/:/.test(cleaned)) score -= 20;
    if (cleaned.length <= 25) score += 5;
    if (wordCount === 1 && cleaned.length <= 3) score -= 30;

    nameCandidates.push({ line: cleaned, idx: i, score });
  }

  // Fallback: if no high-confidence candidate, pick first clean title-cased line
  const bestScore = nameCandidates.length
    ? Math.max(...nameCandidates.map((c) => c.score))
    : 0;
  if (bestScore < 30) {
    for (let i = 0; i < lines.length; i++) {
      if (isUsed(i)) continue;
      const line = cleanOcr(lines[i]).replace(nameLabelRegex, "").trim();
      if (line.length < 3 || line.length > 40) continue;
      if (emailRegex.test(line)) continue;
      if (websiteRegex.test(line)) continue;
      if (/\d{5,}/.test(line)) continue;
      if (companySuffixRegex.test(line)) continue;
      if (isDesignationLine(line)) continue;
      if (nonNameFieldRegex.test(line)) continue;
      if (isAddressOrLocation(line)) continue;
      const words = line.split(/\s+/);
      if (words.length < 2 || words.length > 4) continue;
      if (!words.every((w) => /^[A-Z][a-z]/.test(w) || /^(?:al|bin|ibn|de|van|von|da|di)$/i.test(w))) continue;
      nameCandidates.push({ line, idx: i, score: 30 });
      break;
    }
  }

  let name = "";
  if (nameCandidates.length > 0) {
    nameCandidates.sort((a, b) => b.score - a.score);
    name = titleCase(nameCandidates[0].line);
    markUsed(nameCandidates[0].idx);
  }

  return { name, email, phone: phoneNum, design };
};
